/**
 * <product-video> — a video from the product description (snippets/product-video.liquid).
 *
 * - The file loads only as the video nears the viewport (LOAD_MARGIN); until then the
 *   <video> has no src, so a hidden copy (the description renders in the mobile accordion
 *   and the desktop tabs) never downloads anything.
 * - Plays muted and looped while at least PLAY_RATIO of it is on screen, pauses when it
 *   leaves or the tab is hidden. Reduced motion / Save-Data: no autoplay, a tap plays it.
 * - A tap on the video toggles play; a pause by the visitor sticks until they play again.
 * - The sound button unmutes (and plays). The frame takes the video's own proportions.
 * - One plays at a time: a video waits while another plays, and takes over when that one
 *   stops by itself (left the screen) — not when the visitor paused it.
 */
if (!customElements.get('product-video')) {
  const LOAD_MARGIN = '400px 0px';
  const PLAY_RATIO = 0.5;
  const PREVIEW_AT = 0.5; // s — many of the videos open on a fade from black; the still shows a real frame
  const players = new Set();

  class ProductVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.toggleButton = this.querySelector('[data-video-toggle]');
      this.soundButton = this.querySelector('[data-video-sound]');
      this.fill = this.querySelector('.pdp-video__progress-fill');
      if (!this.video) return;

      this.autoplay = !window.themeUtils?.reduceMotion() && !navigator.connection?.saveData;
      this.userPaused = false;
      this.inView = false;

      this.abortController = new AbortController();
      const { signal } = this.abortController;
      const on = (target, type, handler) => target?.addEventListener(type, handler, { signal });

      on(this.video, 'loadedmetadata', () => {
        this.fitFrame();
        // a stream or a file without a known length reports NaN / Infinity: no preview seek then
        if (!this.started && Number.isFinite(this.video.duration) && this.video.duration > 0) this.video.currentTime = Math.min(PREVIEW_AT, this.video.duration / 2);
      });
      on(this.video, 'play', () => {
        players.forEach((player) => player !== this && player.video.pause());
        this.render();
      });
      on(this.video, 'pause', () => {
        if (!this.userPaused) players.forEach((player) => player !== this && player.update());
        this.render();
      });
      on(this.video, 'volumechange', () => this.render());
      on(this.toggleButton, 'click', () => this.toggle());
      on(this.soundButton, 'click', () => this.toggleSound());
      on(document, 'visibilitychange', () => this.update());

      this.loadObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.load();
        },
        { rootMargin: LOAD_MARGIN }
      );
      this.viewObserver = new IntersectionObserver(
        ([entry]) => {
          this.inView = entry.isIntersecting && entry.intersectionRatio >= PLAY_RATIO;
          this.update();
        },
        { threshold: [0, PLAY_RATIO] }
      );
      players.add(this);
      this.loadObserver.observe(this);
      this.viewObserver.observe(this);
      this.render();
    }

    disconnectedCallback() {
      players.delete(this);
      this.abortController?.abort();
      this.loadObserver?.disconnect();
      this.viewObserver?.disconnect();
      this.stopProgress();
      this.video?.pause();
    }

    load() {
      this.loadObserver?.disconnect();
      if (this.video.getAttribute('src')) return;
      this.video.preload = 'metadata'; // the first frame and size; playing streams the rest
      this.video.src = this.video.dataset.src;
    }

    /** Plays while on screen and visible — unless the visitor paused it, autoplay is off,
     *  or another video is already playing. */
    update() {
      const visible = this.inView && document.visibilityState === 'visible';
      if (!visible) this.video.pause();
      else if (this.autoplay && !this.userPaused && !this.otherPlaying()) this.play();
    }

    otherPlaying() {
      return [...players].some((player) => player !== this && !player.video.paused);
    }

    play() {
      this.load();
      if (!this.started) {
        this.started = true;
        if (this.video.readyState > 0) this.video.currentTime = 0; // back from the preview still
      }
      this.video.play().catch(() => {
        // Autoplay refused (e.g. Low Power Mode on iOS): leave the play button showing.
        this.render();
      });
    }

    toggle() {
      if (this.video.paused) {
        this.userPaused = false;
        this.play();
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    }

    toggleSound() {
      this.video.muted = !this.video.muted;
      if (!this.video.muted && this.video.paused) {
        this.userPaused = false;
        this.play();
      }
    }

    fitFrame() {
      const { videoWidth, videoHeight } = this.video;
      if (!videoWidth || !videoHeight) return;
      this.style.setProperty('--pdp-video-w', videoWidth);
      this.style.setProperty('--pdp-video-h', videoHeight);
    }

    render() {
      const { paused, muted } = this.video;
      this.classList.toggle('is-paused', paused);
      this.classList.toggle('is-muted', muted);
      this.toggleButton?.setAttribute('aria-label', paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      this.soundButton?.setAttribute('aria-label', muted ? this.dataset.unmuteLabel : this.dataset.muteLabel);
      this.soundButton?.setAttribute('aria-pressed', String(!muted));
      if (paused) this.stopProgress();
      else this.startProgress();
    }

    startProgress() {
      if (this.frame || !this.fill) return;
      const step = () => {
        const { currentTime, duration } = this.video;
        this.fill.style.width = duration ? `${(currentTime / duration) * 100}%` : '0%';
        this.frame = requestAnimationFrame(step);
      };
      this.frame = requestAnimationFrame(step);
    }

    stopProgress() {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  customElements.define('product-video', ProductVideo);
}
